import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, Alert, IconButton, Chip, Avatar,
  Checkbox, FormControlLabel, FormGroup, Select, MenuItem, Slider
} from '@mui/material';
import { ArrowLeft, Save, Users as UsersIcon, Sliders, Gamepad2, Image as ImageIcon, Copy, ClipboardPaste, Key, RefreshCw } from 'lucide-react';
import ConfirmDeleteButton from './ConfirmDeleteButton';
import { GAME_TEMPLATES } from '../game/gameTemplates';
import { API_BASE_URL } from '../utils/api';

const ALL_FEATURES = [
  { id: 'storage', label: 'Storage' },
  { id: 'telegram', label: 'Telegram bots' },
  { id: 'ai-tools', label: 'AI tools' },
  { id: 'embeddings', label: 'Embeddings' },
  { id: 'workflows', label: 'Workflows' },
  { id: 'characters', label: 'Characters' },
  { id: 'storyflow', label: 'Storyflow' },
  { id: 'insta', label: 'Insta' },
  { id: 'image-generation', label: 'Generation' },
  { id: 'emb-plot_actions', label: 'Анализ: Сюжет' },
  { id: 'emb-images', label: 'Анализ: Изображения' },
  { id: 'emb-characters', label: 'Анализ: Персонажи' },
  { id: 'emb-chapters', label: 'Анализ: Главы' },
  { id: 'emb-premium_choices', label: 'Анализ: Премиум' },
  { id: 'emb-chapter_hooks', label: 'Анализ: Хуки' },
  { id: 'emb-escalation', label: 'Анализ: Эскалация' }
];

const IMAGE_SIZE_OPTIONS = [
  { id: 'square_hd', label: 'Square HD — 1024×1024' },
  { id: 'square', label: 'Square — 512×512' },
  { id: 'portrait_4_3', label: 'Portrait 4:3 — 768×1024' },
  { id: 'portrait_16_9', label: 'Portrait 16:9 — 576×1024' },
  { id: 'landscape_4_3', label: 'Landscape 4:3 — 1024×768' },
  { id: 'landscape_16_9', label: 'Landscape 16:9 — 1024×576' }
];

const ASPECT_RATIO_OPTIONS = [
  '1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'
];

const PROVIDER_OPTIONS = [
  { id: 'fal', label: 'fal.ai' },
  { id: 'replicate', label: 'Replicate' },
  { id: 'runpod', label: 'Runpod' }
];

// Qwen-Image only: a second RunPod serverless deployment (safe tier) with its
// own endpoint URL, so safe and explicit ratings can hit different endpoints.
const QWEN_PROVIDER_OPTIONS = [
  ...PROVIDER_OPTIONS,
  { id: 'runpod-safe', label: 'Runpod Safe' }
];

const ACCELERATION_OPTIONS = [
  { id: 'none', label: 'None' },
  { id: 'lightning-4steps', label: 'Lightning 4-step' },
  { id: 'lightning-8steps', label: 'Lightning 8-step' }
];

const EDITING_PRESET_LIST = [
  { key: 'imageEdit',    title: 'Image Edit',    hint: 'qwen-image-edit-plus-lora-upscale — general quality/detail enhancement.' },
  { key: 'imageFusion',  title: 'Image Fusion',  hint: 'qwen-image-edit-plus-lora-fusion — blend subject into a new background / lighting.' },
  { key: 'imageRelight', title: 'Image Relight', hint: 'qwen-image-edit-plus-lora-relight — restyle lighting.' }
];

const EMPTY_Z = () => ({
  promptPrefix: '', promptSuffix: '', imageSize: 'square_hd',
  loraUrl: '', loraScale: 0.85, loraTrigger: '',
  explicitLoraUrl: '', explicitLoraScale: 0.85, explicitLoraTrigger: '',
  safeProvider: 'fal', explicitProvider: 'fal'
});

const EMPTY_Q = () => ({
  aspectRatio: '9:16', useTurbo: true,
  runpodEndpoint: '', runpodSafeEndpoint: '', runpodPodId: '',
  // safe path
  promptPrefix: '', promptSuffix: '', negativePromptPrefix: '',
  numInferenceSteps: 25, guidance: 5,
  loraUrl: '', loraScale: 0.7, loraTrigger: '',
  safeProvider: 'replicate',
  acceleration: 'none', accelerationScale: 0.5,
  // explicit path
  explicitPromptPrefix: '', explicitPromptSuffix: '', explicitNegativePromptPrefix: '',
  explicitNumInferenceSteps: 25, explicitGuidance: 5,
  explicitLoraUrl: '', explicitLoraScale: 0.7, explicitLoraTrigger: '',
  explicitProvider: 'replicate',
  explicitAcceleration: 'none', explicitAccelerationScale: 0.5
});

const blockSx = { border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 };

const PRESET_CLIPBOARD_KEY = 'flowcore_generation_preset_clipboard';
const EDITING_CLIPBOARD_KEY = 'flowcore_editing_preset_clipboard';

const EMPTY_EDITING_PRESET = () => ({ loraWeights: '', loraScale: 1, promptPrefix: '' });

function Field({ label, helper, children }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.2 }}>
        {label}
      </Typography>
      {children}
      {helper && (
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
          {helper}
        </Typography>
      )}
    </Box>
  );
}

export default function TeamScreen({ team, users, onBack, onSaved, getToken }) {
  const [name, setName] = useState(team.name || '');
  const [gameTemplate, setGameTemplate] = useState(team.gameTemplate || '');
  const [features, setFeatures] = useState(team.features || []);
  const [members, setMembers] = useState(team.members || []);

  const g0 = team.generation || {};
  const [defaultSafePreset, setDefaultSafePreset] = useState(g0.defaultSafePreset || 'zImageTurbo');
  const [defaultExplicitPreset, setDefaultExplicitPreset] = useState(g0.defaultExplicitPreset || 'zImageTurbo');
  const [promptsPerBatch, setPromptsPerBatch] = useState(g0.promptsPerBatch || 1);
  const [artifactsPerPrompt, setArtifactsPerPrompt] = useState(g0.artifactsPerPrompt || 1);
  const [zImage, setZImage] = useState({ ...EMPTY_Z(), ...(g0.presets?.zImageTurbo || {}) });
  const [qwen, setQwen] = useState({ ...EMPTY_Q(), ...(g0.presets?.qwenImage || {}) });
  const [editingPresets, setEditingPresets] = useState(() => ({
    imageEdit:    { loraWeights: '', loraScale: 1, promptPrefix: '', ...(g0.editingPresets?.imageEdit    || {}) },
    imageFusion:  { loraWeights: '', loraScale: 1, promptPrefix: '', ...(g0.editingPresets?.imageFusion  || {}) },
    imageRelight: { loraWeights: '', loraScale: 1, promptPrefix: '', ...(g0.editingPresets?.imageRelight || {}) }
  }));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [apiKey, setApiKey] = useState(team.apiKey || '');
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    setName(team.name || '');
    setGameTemplate(team.gameTemplate || '');
    setFeatures(team.features || []);
    setMembers(team.members || []);
    const g = team.generation || {};
    setDefaultSafePreset(g.defaultSafePreset || 'zImageTurbo');
    setDefaultExplicitPreset(g.defaultExplicitPreset || 'zImageTurbo');
    setPromptsPerBatch(g.promptsPerBatch || 1);
    setArtifactsPerPrompt(g.artifactsPerPrompt || 1);
    setZImage({ ...EMPTY_Z(), ...(g.presets?.zImageTurbo || {}) });
    setQwen({ ...EMPTY_Q(), ...(g.presets?.qwenImage || {}) });
    setEditingPresets({
      imageEdit:    { loraWeights: '', loraScale: 1, promptPrefix: '', ...(g.editingPresets?.imageEdit    || {}) },
      imageFusion:  { loraWeights: '', loraScale: 1, promptPrefix: '', ...(g.editingPresets?.imageFusion  || {}) },
      imageRelight: { loraWeights: '', loraScale: 1, promptPrefix: '', ...(g.editingPresets?.imageRelight || {}) }
    });
    setApiKey(team.apiKey || '');
  }, [team]);

  const toggleFeature = (id) => {
    setFeatures((prev) => prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]);
  };
  const toggleMember = (id) => {
    setMembers((prev) => prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]);
  };

  const hasGeneration = features.includes('image-generation');

  // Cross-team clipboard for generation presets — backed by localStorage so it
  // survives team switches in the same browser. Copy also writes to the system
  // clipboard for cross-app sharing; paste clears the store so a pasted preset
  // can't be accidentally applied to a third team.
  const [hasClipboard, setHasClipboard] = useState(() => {
    try { return !!localStorage.getItem(PRESET_CLIPBOARD_KEY); } catch { return false; }
  });
  // Separate clipboard slot for the Image Editing Presets block — same
  // copy/paste/clear semantics, distinct key so copying generation presets
  // doesn't overwrite the editing-preset payload (and vice-versa).
  const [hasEditingClipboard, setHasEditingClipboard] = useState(() => {
    try { return !!localStorage.getItem(EDITING_CLIPBOARD_KEY); } catch { return false; }
  });

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === PRESET_CLIPBOARD_KEY) setHasClipboard(!!e.newValue);
      else if (e.key === EDITING_CLIPBOARD_KEY) setHasEditingClipboard(!!e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const copyPresets = useCallback(async () => {
    const payload = {
      defaultSafePreset,
      defaultExplicitPreset,
      promptsPerBatch,
      artifactsPerPrompt,
      presets: { zImageTurbo: zImage, qwenImage: qwen }
    };
    const json = JSON.stringify(payload, null, 2);
    try { localStorage.setItem(PRESET_CLIPBOARD_KEY, json); } catch {}
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try { await navigator.clipboard.writeText(json); } catch {}
    }
    setHasClipboard(true);
    setSuccess('Presets copied');
    setTimeout(() => setSuccess(''), 1200);
  }, [defaultSafePreset, defaultExplicitPreset, promptsPerBatch, artifactsPerPrompt, zImage, qwen]);

  const pastePresets = useCallback(() => {
    let raw;
    try { raw = localStorage.getItem(PRESET_CLIPBOARD_KEY); } catch {}
    if (!raw) { setHasClipboard(false); return; }
    let parsed;
    try { parsed = JSON.parse(raw); } catch {
      setError('Clipboard does not contain valid preset JSON');
      return;
    }
    if (parsed.defaultSafePreset) setDefaultSafePreset(parsed.defaultSafePreset);
    if (parsed.defaultExplicitPreset) setDefaultExplicitPreset(parsed.defaultExplicitPreset);
    if (Number.isFinite(parsed.promptsPerBatch)) setPromptsPerBatch(parsed.promptsPerBatch);
    if (Number.isFinite(parsed.artifactsPerPrompt)) setArtifactsPerPrompt(parsed.artifactsPerPrompt);
    if (parsed.presets?.zImageTurbo) setZImage({ ...EMPTY_Z(), ...parsed.presets.zImageTurbo });
    if (parsed.presets?.qwenImage) setQwen({ ...EMPTY_Q(), ...parsed.presets.qwenImage });
    try { localStorage.removeItem(PRESET_CLIPBOARD_KEY); } catch {}
    setHasClipboard(false);
    setSuccess('Presets pasted — remember to Save');
    setTimeout(() => setSuccess(''), 2000);
  }, []);

  const copyEditingPresets = useCallback(async () => {
    const json = JSON.stringify({ editingPresets }, null, 2);
    try { localStorage.setItem(EDITING_CLIPBOARD_KEY, json); } catch {}
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try { await navigator.clipboard.writeText(json); } catch {}
    }
    setHasEditingClipboard(true);
    setSuccess('Editing presets copied');
    setTimeout(() => setSuccess(''), 1200);
  }, [editingPresets]);

  const pasteEditingPresets = useCallback(() => {
    let raw;
    try { raw = localStorage.getItem(EDITING_CLIPBOARD_KEY); } catch {}
    if (!raw) { setHasEditingClipboard(false); return; }
    let parsed;
    try { parsed = JSON.parse(raw); } catch {
      setError('Clipboard does not contain valid editing-preset JSON');
      return;
    }
    // Accept either { editingPresets: {...} } (full payload) or the bare
    // { imageEdit, imageFusion, imageRelight } shape, so copy/paste is
    // tolerant of users handing JSON around between systems.
    const src = parsed.editingPresets && typeof parsed.editingPresets === 'object'
      ? parsed.editingPresets
      : parsed;
    setEditingPresets({
      imageEdit:    { ...EMPTY_EDITING_PRESET(), ...(src.imageEdit    || {}) },
      imageFusion:  { ...EMPTY_EDITING_PRESET(), ...(src.imageFusion  || {}) },
      imageRelight: { ...EMPTY_EDITING_PRESET(), ...(src.imageRelight || {}) }
    });
    try { localStorage.removeItem(EDITING_CLIPBOARD_KEY); } catch {}
    setHasEditingClipboard(false);
    setSuccess('Editing presets pasted — remember to Save');
    setTimeout(() => setSuccess(''), 2000);
  }, []);

  const save = useCallback(async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const token = await getToken();
      const body = {
        name: name.trim(),
        gameTemplate,
        features,
        members,
        generation: {
          defaultSafePreset,
          defaultExplicitPreset,
          promptsPerBatch,
          artifactsPerPrompt,
          presets: {
            zImageTurbo: zImage,
            qwenImage: qwen
          },
          editingPresets
        }
      };
      const res = await fetch(`${API_BASE_URL}/api/teams/${encodeURIComponent(team.id)}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to save');
      }
      setSuccess('Saved');
      if (onSaved) await onSaved();
      setTimeout(() => setSuccess(''), 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [getToken, team.id, name, gameTemplate, features, members, defaultSafePreset, defaultExplicitPreset, promptsPerBatch, artifactsPerPrompt, zImage, qwen, editingPresets, onSaved]);

  const regenerateApiKey = useCallback(async () => {
    if (apiKey && !window.confirm('Перевыпустить ключ? Старый перестанет работать сразу.')) return;
    setRegenerating(true);
    setError('');
    setSuccess('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/teams/${encodeURIComponent(team.id)}/api-key/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Не удалось сгенерировать ключ');
      }
      const data = await res.json();
      setApiKey(data.apiKey || '');
      setSuccess('Ключ сгенерирован');
      if (onSaved) await onSaved();
      setTimeout(() => setSuccess(''), 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setRegenerating(false);
    }
  }, [getToken, team.id, apiKey, onSaved]);

  const copyApiKey = useCallback(async () => {
    if (!apiKey) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try { await navigator.clipboard.writeText(apiKey); } catch {}
    }
    setSuccess('Ключ скопирован');
    setTimeout(() => setSuccess(''), 1200);
  }, [apiKey]);

  const remove = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/teams/${encodeURIComponent(team.id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to delete');
      }
      if (onSaved) await onSaved();
      onBack();
    } catch (e) {
      setError(e.message);
    }
  }, [getToken, team.id, onBack, onSaved]);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const am = members.includes(a.id) ? 0 : 1;
      const bm = members.includes(b.id) ? 0 : 1;
      if (am !== bm) return am - bm;
      return (a.displayName || a.username || '').localeCompare(b.displayName || b.username || '');
    });
  }, [users, members]);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton size="small" onClick={onBack} sx={{ color: 'text.secondary' }}>
          <ArrowLeft size={18} />
        </IconButton>
        <TextField
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ flex: 1, maxWidth: 360 }}
        />
        {!team.isDefault && (
          <ConfirmDeleteButton onConfirm={remove} />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box display="flex" flexDirection="column" gap={2}>
        {/* Game template + Features */}
        <Box sx={blockSx}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
            <Gamepad2 size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
            Game & Features
          </Typography>
          <Field label="Game template">
            <Select size="small" value={gameTemplate} onChange={(e) => setGameTemplate(e.target.value)} displayEmpty>
              <MenuItem value=""><em>None</em></MenuItem>
              {GAME_TEMPLATES.map((g) => (
                <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
              ))}
            </Select>
          </Field>
          <FormGroup row sx={{ gap: 0.5 }}>
            {ALL_FEATURES.map((f) => (
              <FormControlLabel
                key={f.id}
                sx={{ mr: 1.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={features.includes(f.id)}
                    onChange={() => toggleFeature(f.id)}
                  />
                }
                label={<Typography variant="body2">{f.label}</Typography>}
              />
            ))}
          </FormGroup>
        </Box>

        {/* Presets */}
        {hasGeneration && (
          <Box sx={blockSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ flex: 1 }}>
                <ImageIcon size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                Image Generation Presets
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Copy size={13} />}
                onClick={copyPresets}
                sx={{ minWidth: 0 }}
              >
                Copy
              </Button>
              {hasClipboard && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ClipboardPaste size={13} />}
                  onClick={pastePresets}
                  sx={{ minWidth: 0 }}
                >
                  Paste
                </Button>
              )}
            </Box>

            {/* Shared batching + default presets */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
              <Field label="Prompts per batch">
                <TextField
                  type="number"
                  size="small"
                  inputProps={{ min: 1, max: 16 }}
                  value={promptsPerBatch}
                  onChange={(e) => setPromptsPerBatch(Math.max(1, Math.min(16, parseInt(e.target.value, 10) || 1)))}
                />
              </Field>
              <Field label="Artifacts per prompt">
                <TextField
                  type="number"
                  size="small"
                  inputProps={{ min: 1, max: 16 }}
                  value={artifactsPerPrompt}
                  onChange={(e) => setArtifactsPerPrompt(Math.max(1, Math.min(16, parseInt(e.target.value, 10) || 1)))}
                />
              </Field>
              <Field label="Safe default preset">
                <Select size="small" value={defaultSafePreset} onChange={(e) => setDefaultSafePreset(e.target.value)}>
                  <MenuItem value="zImageTurbo">Z-Image-Turbo</MenuItem>
                  <MenuItem value="qwenImage">Qwen-Image</MenuItem>
                </Select>
              </Field>
              <Field label="Explicit default preset">
                <Select size="small" value={defaultExplicitPreset} onChange={(e) => setDefaultExplicitPreset(e.target.value)}>
                  <MenuItem value="zImageTurbo">Z-Image-Turbo</MenuItem>
                  <MenuItem value="qwenImage">Qwen-Image</MenuItem>
                </Select>
              </Field>
            </Box>

            {/* Z-Image-Turbo preset */}
            <PresetCard title="Z-Image-Turbo">
              <Field label="Prompt prefix">
                <TextField
                  size="small" fullWidth multiline minRows={2}
                  value={zImage.promptPrefix}
                  onChange={(e) => setZImage({ ...zImage, promptPrefix: e.target.value })}
                />
              </Field>
              <Field label="Prompt suffix" helper="Appended after the prompt — if the prompt doesn't end with a period, joined with `. `.">
                <TextField
                  size="small" fullWidth multiline minRows={2}
                  value={zImage.promptSuffix}
                  onChange={(e) => setZImage({ ...zImage, promptSuffix: e.target.value })}
                />
              </Field>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.5 }}>
                <Field label="Image size">
                  <Select size="small" value={zImage.imageSize} onChange={(e) => setZImage({ ...zImage, imageSize: e.target.value })}>
                    {IMAGE_SIZE_OPTIONS.map((o) => (
                      <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </Field>
                <ProviderSelect label="Safe provider" value={zImage.safeProvider} onChange={(v) => setZImage({ ...zImage, safeProvider: v })} />
                <ProviderSelect label="Explicit provider" value={zImage.explicitProvider} onChange={(v) => setZImage({ ...zImage, explicitProvider: v })} />
              </Box>
              <LoraFields
                title="Primary LoRA"
                url={zImage.loraUrl} onUrl={(v) => setZImage({ ...zImage, loraUrl: v })}
                scale={zImage.loraScale} onScale={(v) => setZImage({ ...zImage, loraScale: v })}
                trigger={zImage.loraTrigger} onTrigger={(v) => setZImage({ ...zImage, loraTrigger: v })}
                accent="#a78bfa"
              />
              <LoraFields
                title="Explicit LoRA"
                url={zImage.explicitLoraUrl} onUrl={(v) => setZImage({ ...zImage, explicitLoraUrl: v })}
                scale={zImage.explicitLoraScale} onScale={(v) => setZImage({ ...zImage, explicitLoraScale: v })}
                trigger={zImage.explicitLoraTrigger} onTrigger={(v) => setZImage({ ...zImage, explicitLoraTrigger: v })}
                accent="#f87171"
              />
            </PresetCard>

            {/* Qwen-Image preset */}
            <PresetCard title="Qwen-Image">
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.5 }}>
                <Field label="Aspect ratio">
                  <Select size="small" value={qwen.aspectRatio} onChange={(e) => setQwen({ ...qwen, aspectRatio: e.target.value })}>
                    {ASPECT_RATIO_OPTIONS.map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </Select>
                </Field>
                <Field label="Turbo (fal only)" helper="Use Qwen-Image Turbo distillation. Faster, usually needs lower CFG.">
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={!!qwen.useTurbo}
                        onChange={(e) => setQwen({ ...qwen, useTurbo: e.target.checked })}
                      />
                    }
                    label={<Typography variant="body2">Enabled</Typography>}
                  />
                </Field>
              </Box>
              {(qwen.safeProvider === 'replicate' || qwen.explicitProvider === 'replicate') && (
                <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                  Replicate's qwen-image endpoint ignores <b>inference steps</b> and <b>guidance</b> — those fields take effect only on the fal provider.
                </Alert>
              )}
              {(qwen.safeProvider === 'runpod' || qwen.explicitProvider === 'runpod') && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.5 }}>
                  <Field label="RunPod endpoint" helper="Pod proxy URL (https://<id>-8000.proxy.runpod.net) or serverless API (https://api.runpod.ai/v2/<endpoint-id>). /run, /runsync, /health suffixes are stripped automatically.">
                    <TextField
                      size="small" fullWidth
                      value={qwen.runpodEndpoint}
                      onChange={(e) => setQwen({ ...qwen, runpodEndpoint: e.target.value })}
                      placeholder="https://abc123-8000.proxy.runpod.net"
                    />
                  </Field>
                  <Field label="Pod ID" helper="GPU Pod id (different from the serverless endpoint id). Powers the start/stop pill in the Generation panel. API key lives in the server .env (RUNPOD_API_KEY).">
                    <TextField
                      size="small" fullWidth
                      value={qwen.runpodPodId}
                      onChange={(e) => setQwen({ ...qwen, runpodPodId: e.target.value })}
                      placeholder="podxxxxxxxx"
                    />
                  </Field>
                </Box>
              )}
              {(qwen.safeProvider === 'runpod-safe' || qwen.explicitProvider === 'runpod-safe') && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.5 }}>
                  <Field label="RunPod Safe endpoint" helper="Serverless API of the safe-tier worker (https://api.runpod.ai/v2/<endpoint-id>). Lightning is fused into the worker — set steps ≈ 8 and CFG 1 on the path that uses it.">
                    <TextField
                      size="small" fullWidth
                      value={qwen.runpodSafeEndpoint}
                      onChange={(e) => setQwen({ ...qwen, runpodSafeEndpoint: e.target.value })}
                      placeholder="https://api.runpod.ai/v2/xxxxxxxx"
                    />
                  </Field>
                </Box>
              )}

              {/* Primary (safe) path */}
              <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid rgba(167,139,250,0.25)', borderRadius: '6px', bgcolor: 'rgba(167,139,250,0.04)', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ letterSpacing: 0.4, textTransform: 'uppercase', fontSize: '0.72rem' }}>
                  Primary (Safe)
                </Typography>
                <Field label="Prompt prefix">
                  <TextField
                    size="small" fullWidth multiline minRows={2}
                    value={qwen.promptPrefix}
                    onChange={(e) => setQwen({ ...qwen, promptPrefix: e.target.value })}
                  />
                </Field>
                <Field label="Prompt suffix" helper="Appended after the prompt — if the prompt doesn't end with a period, joined with `. `.">
                  <TextField
                    size="small" fullWidth multiline minRows={2}
                    value={qwen.promptSuffix}
                    onChange={(e) => setQwen({ ...qwen, promptSuffix: e.target.value })}
                  />
                </Field>
                <Field label="Negative prompt prefix" helper="Prepended to every negative prompt.">
                  <TextField
                    size="small" fullWidth multiline minRows={2}
                    value={qwen.negativePromptPrefix}
                    onChange={(e) => setQwen({ ...qwen, negativePromptPrefix: e.target.value })}
                  />
                </Field>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
                  <ProviderSelect label="Provider" value={qwen.safeProvider} onChange={(v) => setQwen({ ...qwen, safeProvider: v })} options={QWEN_PROVIDER_OPTIONS} />
                  <Field label="Inference steps">
                    <TextField
                      type="number" size="small"
                      inputProps={{ min: 1, max: 50 }}
                      value={qwen.numInferenceSteps}
                      onChange={(e) => setQwen({ ...qwen, numInferenceSteps: Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 25)) })}
                    />
                  </Field>
                  <Field label="Guidance (CFG)">
                    <TextField
                      type="number" size="small"
                      inputProps={{ min: 0, max: 20, step: 0.5 }}
                      value={qwen.guidance}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setQwen({ ...qwen, guidance: Number.isFinite(v) ? Math.max(0, Math.min(20, v)) : 5 });
                      }}
                    />
                  </Field>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.5 }}>
                  <Field label="Acceleration LoRA" helper="RunPod only — stacks Lightning speed-LoRA on top of your LoRA. Pick the variant matching your step count.">
                    <Select size="small" value={qwen.acceleration} onChange={(e) => setQwen({ ...qwen, acceleration: e.target.value })}>
                      {ACCELERATION_OPTIONS.map((o) => (
                        <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </Field>
                  {qwen.acceleration !== 'none' && (
                    <Field label={`Acceleration scale — ${Number(qwen.accelerationScale).toFixed(2)}`}>
                      <Slider size="small" min={0} max={2} step={0.05}
                        value={qwen.accelerationScale}
                        onChange={(_, v) => setQwen({ ...qwen, accelerationScale: Array.isArray(v) ? v[0] : v })}
                        sx={{ color: '#a78bfa' }}
                      />
                    </Field>
                  )}
                </Box>
                <LoraFields
                  title="LoRA"
                  url={qwen.loraUrl} onUrl={(v) => setQwen({ ...qwen, loraUrl: v })}
                  scale={qwen.loraScale} onScale={(v) => setQwen({ ...qwen, loraScale: v })}
                  trigger={qwen.loraTrigger} onTrigger={(v) => setQwen({ ...qwen, loraTrigger: v })}
                  accent="#a78bfa"
                />
              </Box>

              {/* Explicit path */}
              <Box sx={{ p: 1.5, border: '1px solid rgba(248,113,113,0.25)', borderRadius: '6px', bgcolor: 'rgba(248,113,113,0.04)', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ letterSpacing: 0.4, textTransform: 'uppercase', fontSize: '0.72rem' }}>
                  Explicit
                </Typography>
                <Field label="Prompt prefix">
                  <TextField
                    size="small" fullWidth multiline minRows={2}
                    value={qwen.explicitPromptPrefix}
                    onChange={(e) => setQwen({ ...qwen, explicitPromptPrefix: e.target.value })}
                  />
                </Field>
                <Field label="Prompt suffix" helper="Appended after the prompt — if the prompt doesn't end with a period, joined with `. `.">
                  <TextField
                    size="small" fullWidth multiline minRows={2}
                    value={qwen.explicitPromptSuffix}
                    onChange={(e) => setQwen({ ...qwen, explicitPromptSuffix: e.target.value })}
                  />
                </Field>
                <Field label="Negative prompt prefix" helper="Prepended to every negative prompt.">
                  <TextField
                    size="small" fullWidth multiline minRows={2}
                    value={qwen.explicitNegativePromptPrefix}
                    onChange={(e) => setQwen({ ...qwen, explicitNegativePromptPrefix: e.target.value })}
                  />
                </Field>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
                  <ProviderSelect label="Provider" value={qwen.explicitProvider} onChange={(v) => setQwen({ ...qwen, explicitProvider: v })} options={QWEN_PROVIDER_OPTIONS} />
                  <Field label="Inference steps">
                    <TextField
                      type="number" size="small"
                      inputProps={{ min: 1, max: 50 }}
                      value={qwen.explicitNumInferenceSteps}
                      onChange={(e) => setQwen({ ...qwen, explicitNumInferenceSteps: Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 25)) })}
                    />
                  </Field>
                  <Field label="Guidance (CFG)">
                    <TextField
                      type="number" size="small"
                      inputProps={{ min: 0, max: 20, step: 0.5 }}
                      value={qwen.explicitGuidance}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setQwen({ ...qwen, explicitGuidance: Number.isFinite(v) ? Math.max(0, Math.min(20, v)) : 5 });
                      }}
                    />
                  </Field>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.5 }}>
                  <Field label="Acceleration LoRA" helper="RunPod only — stacks Lightning speed-LoRA on top of your LoRA. Pick the variant matching your step count.">
                    <Select size="small" value={qwen.explicitAcceleration} onChange={(e) => setQwen({ ...qwen, explicitAcceleration: e.target.value })}>
                      {ACCELERATION_OPTIONS.map((o) => (
                        <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </Field>
                  {qwen.explicitAcceleration !== 'none' && (
                    <Field label={`Acceleration scale — ${Number(qwen.explicitAccelerationScale).toFixed(2)}`}>
                      <Slider size="small" min={0} max={2} step={0.05}
                        value={qwen.explicitAccelerationScale}
                        onChange={(_, v) => setQwen({ ...qwen, explicitAccelerationScale: Array.isArray(v) ? v[0] : v })}
                        sx={{ color: '#f87171' }}
                      />
                    </Field>
                  )}
                </Box>
                <LoraFields
                  title="LoRA"
                  url={qwen.explicitLoraUrl} onUrl={(v) => setQwen({ ...qwen, explicitLoraUrl: v })}
                  scale={qwen.explicitLoraScale} onScale={(v) => setQwen({ ...qwen, explicitLoraScale: v })}
                  trigger={qwen.explicitLoraTrigger} onTrigger={(v) => setQwen({ ...qwen, explicitLoraTrigger: v })}
                  accent="#f87171"
                />
              </Box>
            </PresetCard>
          </Box>
        )}

        {/* Image Editing Presets */}
        {hasGeneration && (
          <Box sx={blockSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ flex: 1 }}>
                <ImageIcon size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                Image Editing Presets
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Copy size={13} />}
                onClick={copyEditingPresets}
                sx={{ minWidth: 0 }}
              >
                Copy
              </Button>
              {hasEditingClipboard && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ClipboardPaste size={13} />}
                  onClick={pasteEditingPresets}
                  sx={{ minWidth: 0 }}
                >
                  Paste
                </Button>
              )}
            </Box>
            {EDITING_PRESET_LIST.map(({ key, title, hint }) => {
              const p = editingPresets[key];
              const setP = (patch) => setEditingPresets({ ...editingPresets, [key]: { ...p, ...patch } });
              return (
                <PresetCard key={key} title={title}>
                  {hint && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                      {hint}
                    </Typography>
                  )}
                  <Field label="Prompt prefix" helper="Prepended to the user's prompt from the editing form.">
                    <TextField
                      size="small" fullWidth multiline minRows={2}
                      value={p.promptPrefix}
                      onChange={(e) => setP({ promptPrefix: e.target.value })}
                    />
                  </Field>
                  <Field label="LoRA weights URL" helper="Optional .safetensors URL for the qwen-image-edit-plus LoRA.">
                    <TextField
                      size="small" fullWidth
                      value={p.loraWeights}
                      onChange={(e) => setP({ loraWeights: e.target.value })}
                    />
                  </Field>
                  <Field label={`LoRA scale — ${Number(p.loraScale).toFixed(2)}`}>
                    <Slider size="small" min={0} max={1.5} step={0.05}
                      value={p.loraScale}
                      onChange={(_, v) => setP({ loraScale: Array.isArray(v) ? v[0] : v })}
                      sx={{ color: '#a78bfa' }}
                    />
                  </Field>
                </PresetCard>
              );
            })}
          </Box>
        )}

        {/* API Access — per-team key for external services hitting /api/v1 */}
        {hasGeneration && (
          <Box sx={blockSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ flex: 1 }}>
                <Key size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                API Access
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshCw size={13} />}
                onClick={regenerateApiKey}
                disabled={regenerating}
                sx={{ minWidth: 0 }}
              >
                {apiKey ? 'Regenerate' : 'Generate'}
              </Button>
              {apiKey && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Copy size={13} />}
                  onClick={copyApiKey}
                  sx={{ minWidth: 0 }}
                >
                  Copy
                </Button>
              )}
            </Box>
            <Field
              label="API key"
              helper="Используется внешними сервисами. Шлите как Authorization: Bearer <ключ> на /api/v1/image-generation. См. docs/external-image-generation-api.md."
            >
              <TextField
                size="small"
                fullWidth
                value={apiKey}
                placeholder="(нет — нажми Generate)"
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              />
            </Field>
          </Box>
        )}

        {/* Members */}
        <Box sx={blockSx}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
            <UsersIcon size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
            Members — {members.length}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 360, overflowY: 'auto' }}>
            {sortedUsers.map((u) => {
              const checked = members.includes(u.id);
              const userName = u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username || '—';
              return (
                <FormControlLabel
                  key={u.id}
                  sx={{ m: 0, py: 0.25, borderRadius: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}
                  control={<Checkbox size="small" checked={checked} onChange={() => toggleMember(u.id)} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {u.picture
                        ? <Avatar src={u.picture} sx={{ width: 24, height: 24 }} />
                        : <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>{(userName || '?').slice(0, 1).toUpperCase()}</Avatar>}
                      <Typography variant="body2">{userName}</Typography>
                      {u.username && <Typography variant="caption" color="text.secondary">@{u.username}</Typography>}
                      {u.role === 'admin' && <Chip label="admin" size="small" sx={{ height: 18, fontSize: '0.6rem' }} />}
                    </Box>
                  }
                />
              );
            })}
          </Box>
        </Box>

        <Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<Save size={16} />}
            onClick={save}
            disabled={saving || !name.trim()}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function PresetCard({ title, children }) {
  return (
    <Box sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}>
        <Sliders size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function ProviderSelect({ label, value, onChange, options = PROVIDER_OPTIONS }) {
  return (
    <Field label={label}>
      <Select size="small" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((p) => (
          <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
        ))}
      </Select>
    </Field>
  );
}

function LoraFields({ title, url, onUrl, scale, onScale, trigger, onTrigger, accent }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
      <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ letterSpacing: 0.4, textTransform: 'uppercase', fontSize: '0.72rem' }}>
        {title}
      </Typography>
      <Field label="URL">
        <TextField size="small" fullWidth value={url} onChange={(e) => onUrl(e.target.value)} />
      </Field>
      {onScale && (
        <Field label={`Scale — ${Number(scale).toFixed(2)}`}>
          <Slider size="small" min={0} max={1.5} step={0.05} value={scale} onChange={(_, v) => onScale(Array.isArray(v) ? v[0] : v)} sx={{ color: accent }} />
        </Field>
      )}
      <Field label="Trigger word (optional)">
        <TextField size="small" fullWidth value={trigger} onChange={(e) => onTrigger(e.target.value)} />
      </Field>
    </Box>
  );
}
